import { ActivatedRouteSnapshot } from '@angular/router';

export enum ForbiddenLayout {
    DASHBOARD = 'dashboard',
    TAG_RATOEIRA = 'tag-ratoeira',
    CAMPAIGN_CREATOR = 'campaign-creator',
}

export interface ForbiddenMixPanel {
    page: string;
    place: string;
    menu: string;
}

interface ForbiddenLayoutConfig {
    pageSectionKey: string;
    mixPanel: ForbiddenMixPanel;
}

export interface ForbiddenState {
    layout: ForbiddenLayout;
    pageSectionKey: string;
    mixPanel: ForbiddenMixPanel;
}

export interface MetricMockItem {
    id: string;
    name: string;
    value: number;
    trend: 'neutral';
}

export interface MetricMockSection {
    title: string;
    items: MetricMockItem[];
}

export interface TagMockRow {
    id: string;
    name: string;
    platform: string;
    date: string;
}

const LAYOUT_CONFIG: Record<ForbiddenLayout, ForbiddenLayoutConfig> = {
    [ForbiddenLayout.DASHBOARD]: {
        pageSectionKey: 'planForbidden.sections.dashboard',
        mixPanel: {
            page: 'dashboard-metricas',
            place: 'modal-dashboard-forbidden',
            menu: 'analytics',
        },
    },
    [ForbiddenLayout.TAG_RATOEIRA]: {
        pageSectionKey: 'planForbidden.sections.tagRatoeira',
        mixPanel: {
            page: 'tag-ratoeira',
            place: 'modal-tag-ratoeira-forbidden',
            menu: 'tag-ratoeira',
        },
    },
    [ForbiddenLayout.CAMPAIGN_CREATOR]: {
        pageSectionKey: 'planForbidden.sections.campaignCreator',
        mixPanel: {
            page: 'campaign-creator',
            place: 'modal-campaign-creator-forbidden',
            menu: 'management',
        },
    },
};

export interface PremiumGatedNavRoutes {
    allowed: string[];
    forbidden: string[];
}

/** Sidebar nav targets tied to each forbidden layout. Extend when adding a new premium-gated section. */
export const PREMIUM_GATED_NAV_ROUTES: Record<ForbiddenLayout, PremiumGatedNavRoutes> = {
    [ForbiddenLayout.DASHBOARD]: {
        allowed: ['/v2/analytics/financial-dashboard-new'],
        forbidden: ['/v2/analytics/forbidden'],
    },
    [ForbiddenLayout.TAG_RATOEIRA]: {
        allowed: ['/v2/tag-ratoeira/list'],
        forbidden: ['/v2/tag-ratoeira/forbidden'],
    },
    [ForbiddenLayout.CAMPAIGN_CREATOR]: {
        allowed: ['/v2/campaign-creator/google-ads/search/wizard'],
        forbidden: ['/v2/campaign-creator/forbidden'],
    },
};

export const TAG_MOCK_ROWS: TagMockRow[] = [
    { id: '1', name: 'Tag Checkout Principal', platform: 'Google Ads', date: '12/03/2026' },
    { id: '2', name: 'Tag VSL — Página de vendas', platform: 'Meta Ads', date: '08/03/2026' },
    { id: '3', name: 'Tag Lead — Formulário', platform: 'Google Ads', date: '01/03/2026' },
    { id: '4', name: 'Tag Upsell', platform: 'Google Ads', date: '24/02/2026' },
];

export const WIZARD_STEPS = [
    'Campanha',
    'Objetivo e funil',
    'Orçamento',
    'Segmentação',
    'Conta',
    'Preview',
    'Resultado',
] as const;

export const CAMPAIGN_FIELDS = ['URL da campanha', 'URL para extração', 'Idioma'] as const;

const randomMetric = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

export function buildMetricSections(): MetricMockSection[] {
    return ['Visão de Performance', 'Aquisição e Crescimento'].map((title) => ({
        title,
        items: Array.from({ length: 8 }, (_, index) => ({
            id: String(index),
            name: `Metric ${index + 1}`,
            value: randomMetric(0, 1000),
            trend: 'neutral' as const,
        })),
    }));
}

export function resolveLayout(value: unknown): ForbiddenLayout {
    if (value === ForbiddenLayout.TAG_RATOEIRA || value === ForbiddenLayout.CAMPAIGN_CREATOR) {
        return value;
    }

    return ForbiddenLayout.DASHBOARD;
}

export function parseForbiddenState(route: ActivatedRouteSnapshot): ForbiddenState {
    const layout = resolveLayout(route.data['forbiddenMock']);
    const config = LAYOUT_CONFIG[layout];
    const routeMixPanel = route.data['forbiddenMixPanel'];

    return {
        layout,
        pageSectionKey: config.pageSectionKey,
        mixPanel: {
            ...config.mixPanel,
            ...(routeMixPanel && typeof routeMixPanel === 'object' ? routeMixPanel : {}),
        },
    };
}
